import React, { useEffect, useRef, useState } from "react";
import MyDialogContent from "./MyDialogContent";
import RHFWrapper from "./RHFWrapper";
import StepBar from "./StepBar";
import { Button } from "./ui/button";
import RhfInput from "./rhfFields/RhfInput";
import Grid from "./Grid";
import RhfPhone from "./rhfFields/RhfPhone";
import { organizationValidationSchema } from "@/utils/ValidataionSchemas";
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  step1OrgInitialValues,
  step2OrgInitialValues,
  organizationInitialValues,
  step3OrgInitialValues,
} from "@/utils/InitialValues";
import { toast } from "sonner";
import { InfoIcon } from "lucide-react";
import RhfRadio from "./rhfFields/RhfRadio";
import RhfChecked from "./rhfFields/RhfChecked";
import CityStateZipcode from "./CityStateZipcode";
import { excludeKeys } from "@/utils/helpers";
import RhfAddressAutoComplete from "./rhfFields/RhfAddressAutoComplete";
import { US_ORG_TIMEZONES } from "@/constants/usOrgTimezones";

// Grid Component (assuming this is your implementation)

function OrgTimezoneField() {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name="timezone"
      render={({ field }) => (
        <FormItem className="min-w-0">
          <FormLabel>Timezone*</FormLabel>
          <Select
            onValueChange={field.onChange}
            value={field.value ? String(field.value) : undefined}
          >
            <FormControl>
              <SelectTrigger className="h-10 w-full">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
            </FormControl>
            <SelectContent position="popper" className="max-h-[280px] z-[10001]">
              {US_ORG_TIMEZONES.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function readApiError(err: unknown): string {
  const data = (err as { response?: { data?: { message?: unknown; errors?: unknown } } })
    ?.response?.data;
  const parts: string[] = [];
  const push = (value: unknown) => {
    if (typeof value === "string" && value.trim()) parts.push(value.trim());
    else if (Array.isArray(value)) value.forEach(push);
    else if (value && typeof value === "object") Object.values(value).forEach(push);
  };
  if (data?.message) push(data.message);
  if (data?.errors) push(data.errors);
  if (parts.length) return [...new Set(parts)].join("\n");
  if (err instanceof Error && err.message) return err.message;
  return "Something went wrong";
}

type Props = {
  defaultValues: any;
  onSubmit: (data: any, cb: () => void, step: number, isNew: boolean) => void;
  heading: string;
  isOpen: boolean;
  btnLoading: boolean;
  onClose: () => void;
};

// Initial Values Schema

const OrganizationForm = ({
  defaultValues,
  onSubmit,
  heading,
  isOpen,
  btnLoading,
  onClose,
}: Props) => {
  const [step, setStep] = useState(0);
  const isNew = defaultValues?._id ? false : step == 0;
  const [isSkipped, setIsSkipped] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const apiErrorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setStep(0);
    setApiError(null);
  }, [isOpen]);

  useEffect(() => {
    setApiError(null);
  }, [step]);

  useEffect(() => {
    if (!apiError) return;
    apiErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [apiError]);

  const steps = [
    { stepNumber: 1, label: "General Details" },
    { stepNumber: 2, label: "Biller Details" },
    { stepNumber: 3, label: "Billing Information" },
  ];

  console.log({ step });
  async function submitStep(
    data: any,
    onSuccess: () => void,
    submitStepNumber: number,
  ) {
    setApiError(null);
    try {
      await onSubmit(data, onSuccess, submitStepNumber, isNew);
    } catch (error) {
      setApiError(readApiError(error));
    }
  }

  function nextStep(data: any, isDirty: boolean) {
    if (step == 1) setIsSkipped(false);
    if (step == 2) {
      console.log("FINAL ,", { data, step, isSkipped });
      let modifiedData = isSkipped
        ? excludeKeys(data, isSkipped ? Object.keys(step2OrgInitialValues) : [])
        : data;
        if(isDirty){
          void submitStep(modifiedData, () => {
            onClose();
          }, 3);
        }
        else{
          onClose();
        }
    } else {
      if(isDirty){
        void submitStep(data, () => {
          setStep((p) => Math.min(2, p + 1));
        }, step + 1);
      }
      else{
            setStep((p) => Math.min(2, p + 1));

      }
    }
  }
  function skipStep() {
    if (step == 1) setIsSkipped(true);

    if (step == 2) {
      onClose();
      return;
    }
    setTimeout(() => {
      setStep((p) => Math.min(2, p + 1));
    }, 0);
  }

  function backStep() {
    setStep((p) => Math.max(0, p - 1));
  }

  // Merge defaultValues with organizationInitialValues to ensure all fields have defaults
  const { timeZone: legacyTimeZone, ...restDefaultValues } = defaultValues || {};
  const mergedInitialValues = {
    ...organizationInitialValues,
    ...restDefaultValues,
    // API expects `timezone`; older responses may use `timeZone`
    timezone:
      restDefaultValues?.timezone ??
      legacyTimeZone ??
      organizationInitialValues.timezone,
    // Ensure providerGroupType defaults to "Provider" if not set
    providerGroupType:
      restDefaultValues?.providerGroupType ||
      defaultValues?.providerGroupType ||
      "Provider NPI",
  };

  useEffect(() => {
    setIsSkipped(false);
  }, []);
  return (
    <>
      <MyDialogContent
        isWide={true}
        heading={heading}
        headerComponent={<StepBar steps={steps} currentStep={step + 1} />}
        className="h-[85vh]"
      >
        <RHFWrapper
          className="space-y-4 relative"
          initialValues={mergedInitialValues}
          onSubmit={async (data, methods) => {
            // this function wont run;
            let validateFieldValues = Object.keys(step3OrgInitialValues);
            const isValid = await methods.trigger(validateFieldValues);
            console.log(isValid);
            if (!isValid) {
              return;
            }
            void submitStep(data, () => {
              onClose();
            }, 3);
          }}
          isValidate={true}
          validateSchema={organizationValidationSchema}
        >
          <div className="space-y-4 flex-1  ">
            {step === 0 && <Step01 />}
            {step === 1 && <Step02 />}
            {step === 2 && <Step03 />}
          </div>
          {apiError && (
            <div
              ref={apiErrorRef}
              className="scroll-mb-24 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 whitespace-pre-line"
            >
              {apiError}
            </div>
          )}

          <div className="flex items-center justify-between  sticky bottom-0 w-full bg-white">
            <div className="flex items-center justify-between max-w-2xl mx-auto w-full gap-3">
              <Button
                size={"sm"}
                onClick={backStep}
                className="flex-1"
                variant={"outline"}
                disabled={step === 0}
                type="button"
              >
                {step === 0 ? "Skip" : "Back"}
              </Button>
              <Skipbutton skipStep={skipStep} step={step} />

              <SubmitBtnWithTrigger
                step={step}
                nextStep={nextStep}
                btnLoading={btnLoading}
              />
            </div>
          </div>
        </RHFWrapper>
      </MyDialogContent>
    </>
  );
};

// Step 1: General Details
const Step01 = () => {
  return (
    <>
      <Grid count={3} gap={4}>
        <RhfInput
          name="ownerFirstName"
          label="POC First Name"
          required
          placeholder="First Name"
        />
        <RhfInput
          name="ownerMiddleName"
          label="POC Middle Name"
          placeholder="Middle Name"
        />
        <RhfInput
          name="ownerLastName"
          label="POC Last Name"
          required
          placeholder="Last Name"
        />
      </Grid>

      <RhfInput
        name="organizationName"
        label="Name of Organization"
        required
        placeholder="Organization Name"
      />
      <RhfRadio
        name="providerGroupType"
        label="Provider / Group NPI"
        required
        options={[
          { label: "Provider NPI", value: "Provider NPI" },
          { label: "Group NPI", value: "Group NPI" },
        ]}
      />
      <RhfInput
        name="providerGroupNPI"
        label="Provider / Group NPI"
        required
        isNumber
        maxLength={10}
        placeholder="Provider Group NPI"
      />

      <Grid
        count={{ sm: 1, md: 3 }}
        gap={4}
      >
        <RhfPhone
          name="phoneNumber"
          label="Phone Number"
          required
          placeholder="Phone Number"
        />
        <RhfInput
          name="email"
          label="Email"
          type="email"
          required
          placeholder="Email"
        />
        <OrgTimezoneField />
      </Grid>

      <div className="space-y-4 ">
        <h2 className="font-bold">Address </h2>
        <RhfAddressAutoComplete
        key={'step01_address'}
          name="address"
          label="Address"
          onAddressSelect={(data) => {}}
          required
          modifyValuesMapping={{
            address: "address1",
            street: "address2",
            city: "city",
            state: "state",
            zipCode: "zipcode",
          }}
        />
        {/* <RhfInput
          name="address"
          label="Address"
          placeholder="Address"
          required
        /> */}

        <RhfInput
          name="street"
          label="Street / Address 2"
          placeholder="Street / Address 2"
        />

        {/* <Grid count={3} gap={4}> */}
        <CityStateZipcode
          cityName="city"
          stateName="state"
          zipcodeName="zipCode"
          count={3}
          gap={4}
        />
        {/* 
          <RhfInput name="city" label="City" required placeholder="City" />
          <RhfInput name="state" label="State" required placeholder="State" />
          <RhfInput
            name="zipCode"
            label="Zip Code"
            required
            isNumber={true}
            maxLength={5}
            placeholder="Zip code"
          />
        </Grid> */}
      </div>
      {/* <div className="space-y-4 ">
        <h2 className="font-bold">Address 2</h2>
        <RhfInput name="address2" label="Address" placeholder="Address" />

        <RhfInput name="street2" label="Street" placeholder="Street" />

        <Grid count={3} gap={4}>
          <RhfInput name="city2" label="City" placeholder="City" />
          <RhfInput name="state2" label="State" placeholder="State" />
          <RhfInput
            name="zipCode2"
            label="Zip Code"
            isNumber={true}
            maxLength={5}
            placeholder="Zip code"
          />
        </Grid>
      </div> */}
    </>
  );
};

// Step 2: Biller Details
const Step02 = () => {
  return (
    <>
      <Grid count={3} gap={4}>
        <RhfInput
          name="billerFirstName"
          label="First Name"
          required
          placeholder="First Name"
        />
        <RhfInput
          name="billerMiddleName"
          label="Middle Name"
          placeholder="Middle Name"
        />
        <RhfInput
          name="billerLastName"
          label="Last Name"
          required
          placeholder="Last Name"
        />
      </Grid>

      <Grid count={2} gap={4}>
        <RhfPhone
          name="billerPhoneNumber"
          label="Phone Number"
          required
          placeholder="Phone Number"
        />
        <RhfInput
          name="billerEmail"
          label="Email"
          required
          placeholder="Email"
          type="email"
        />
        {/* <RhfInput
          name="billerLocation"
          label="Location"
          required
          placeholder="Location"
        /> */}
      </Grid>

      <RhfAddressAutoComplete
        key={'step02_address'}

        name="billerAddress"
        label="Address"
        onAddressSelect={(data) => {}}
        required
        modifyValuesMapping={{
          billerAddress: "address1",
          billerStreet: "address2",
          billerCity: "city",
          billerState: "state",
          billerZipCode: "zipcode",
        }}
      />
      {/* <RhfInput
        name="billerAddress"
        label="Address"
        placeholder="Address"
        required
      /> */}

      <RhfInput
        name="billerStreet"
        label="Address 2 / Street"
        required
        placeholder="Street"
      />

      <CityStateZipcode
        cityName="billerCity"
        stateName="billerState"
        zipcodeName="billerZipCode"
        count={3}
        gap={4}
      />

      {/* <Grid count={3} gap={4}>
        <RhfInput name="billerCity" label="City" required placeholder="City" />
        <RhfInput
          name="billerState"
          label="State"
          required
          placeholder="State"
        />
        <RhfInput
          name="billerZipCode"
          label="Zip Code"
          required
          isNumber
          maxLength={5}
          placeholder="Zip code"
        />
      </Grid> */}

      {/* <RhfInput
        name="billingPortalUsed"
        label="Billing Portal Used"
        placeholder="Billing Portal Used"
      /> */}
      <SkipNote />
    </>
  );
};

// Step 3: Billing Information
const Step03 = () => {
  return (
    <>
      {/* ---------------------- Billing Info Section ---------------------- */}
      <div className="space-y-4">
        <h1 className="text-lg font-semibold">Billing Information</h1>

        <RhfInput
          name="groupName"
          label="Group Name"
          required
          placeholder="Group Name"
        />
 <RhfAddressAutoComplete
        key={'step03_address'}

        name="billingAddress"
        label="Address"
        onAddressSelect={(data) => {}}
        required
        modifyValuesMapping={{
          billingAddress: "address1",
          billingStreet: "address2",
          billingCity: "city",
          billingState: "state",
          billingZipCode: "zipcode",
        }}
      />
        {/* <RhfInput
          name="billingAddress"
          label="Address"
          required
          placeholder="Address"
        /> */}

        <RhfInput
          name="billingStreet"
          label="Street / Address 2"
          required
          placeholder="Street / Address 2"
        />

        <CityStateZipcode
          cityName="billingCity"
          stateName="billingState"
          zipcodeName="billingZipCode"
          count={3}
          gap={4}
        />

        {/* <Grid count={3} gap={4}>
          <RhfInput
            name="billingCity"
            label="City"
            required
            placeholder="City"
          />
          <RhfInput
            name="billingState"
            label="State"
            required
            placeholder="State"
          />
          <RhfInput
            name="billingZipCode"
            label="Zip Code"
            required
            isNumber
            maxLength={5}
            placeholder="Zip code"
          />
        </Grid> */}
      </div>

      {/* ---------------------- Taxation Details Section ---------------------- */}
      <div className="space-y-4 mt-8 border-t pt-4">
        <h1 className="text-lg font-semibold">Taxation Details</h1>

        <Grid count={2} gap={4}>
          <RhfPhone
            name="primaryPhoneNumber"
            label="Primary Phone Number"
            required
            placeholder="Phone Number"
          />
          <RhfInput name="primaryFax" label="Primary Fax" placeholder="Fax" />
        </Grid>

        <Grid count={3} gap={4}>
          <RhfInput
            name="groupTaxId"
            label="Group Tax Id"
            placeholder="Group Tax Id"
          />
          <RhfInput name="groupNPI" label="Group NPI" placeholder="Group NPI" />
          <RhfInput name="ptan" label="PTAN" placeholder="PTAN" />
        </Grid>

        <Grid count={3} gap={4}>
          <RhfInput
            name="tradingPartnerId"
            label="Trading Partner Id"
            placeholder="Trading Partner Id"
          />
          <RhfInput name="tin" label="TIN" placeholder="TIN" />
          <RhfInput
            name="medicalId"
            label="Medical Id"
            placeholder="Medical Id"
          />
        </Grid>
      </div>

      <RhfInput
        name="taxonomyCode"
        label="Taxonomy Code"
        placeholder="Taxonomy Code"
      />
      <SkipNote />
    </>
  );
};

const SubmitBtnWithTrigger = ({
  step,
  nextStep,
  btnLoading,
}: {
  step: number;
  nextStep: (data: any, isDirty: boolean) => void;
  btnLoading: boolean;
}) => {
  const {
    trigger,
    getValues,
    formState: { isDirty, dirtyFields },
  } = useFormContext();
  async function checkFieldAndNext() {
    let validateFieldValues = [""];
    let isDirty = false;
    if (step == 0) {
      validateFieldValues = Object.keys(step1OrgInitialValues);
    } else if (step == 1) {
      validateFieldValues = Object.keys(step2OrgInitialValues);
    } else {
      validateFieldValues = Object.keys(step3OrgInitialValues);
    }

    const isValid = await trigger(validateFieldValues as string[]);
    console.log({ isValid, validateFieldValues });
    Object.keys(dirtyFields).forEach(dKey=> {
      if(!isDirty){

      isDirty =   validateFieldValues.includes(dKey)
      }
    })
        console.log({ isValid, getValues: getValues(), isDirty });
    if (isValid) {
      nextStep(getValues(), isDirty);
    }
  }

  return (
    <>
      {step <= 2 ? (
        <Button
          size={"sm"}
          className="flex-1"
          disabled={btnLoading}
          onClick={async () => await checkFieldAndNext()}
          variant={"default"}
          type={"button"}
        >
          {step == 0 ? "Save & Next" : "Next"}
        </Button>
      ) : (
        <Button
          size={"sm"}
          className="flex-1"
          variant={"default"}
          disabled={btnLoading}
          type={"submit"}
        >
          {"Save and Continue"}
        </Button>
      )}
    </>
  );
};

const Skipbutton = ({ skipStep, step }: any) => {
  const methods = useFormContext();
  const values = methods.watch(); // watch all form values

  // check if all keys in step2OrgInitialValues are empty
  const isAllEmpty = Object.keys(
    step == 1 ? step2OrgInitialValues : step3OrgInitialValues,
  ).every((key) => {
    const v = values[key];
    return v === "" || v === null || v === undefined;
  });

  return (
    <>
      {step != 0 && (
        <Button
          size={"sm"}
          onClick={() => skipStep()}
          className="flex-1"
          variant={"secondary"}
          disabled={!isAllEmpty}
          type="button"
        >
          Skip
        </Button>
      )}
    </>
  );
};

const SkipNote = ({ variant = "default" }) => {
  const text = {
    default:
      "This section is optional — you can skip it for now and add billing information later from the organization settings. Skipping won’t affect your ability to continue.",
    short: "Optional — you can skip this step and fill it later from settings.",
    friendly:
      "Not ready to add billing info? No problem — skip this step and come back anytime from Organization Settings.",
    detailed:
      "This step is optional. If you choose to skip, you can still use the core features now and add or update billing/taxation information later from the Organization Settings page. We’ll remind you if any required data is missing when you try to perform billing actions.",
  }[variant];

  return (
    <div className="flex items-start gap-3 p-4 rounded-lg border border-blue-100 bg-blue-50">
      <div className="mt-0.5">
        <InfoIcon className="h-5 w-5 text-blue-600" />
      </div>

      <div>
        <p className="text-sm font-medium text-slate-700">
          You can skip this step
        </p>
        <p className="mt-1 text-sm text-slate-600">{text}</p>
      </div>
    </div>
  );
};

export default OrganizationForm;
